import React, { useState } from 'react';
import { 
  useGetSuperAdminDashboardQuery, 
  useRefreshDashboardMutation,
  SuperAdminDashboardData
} from '../api/dashboardApiSlice';

import { Card } from '../../../components/ui/card';
import { showToast } from '../../../components/ui/Toast';
import { Button } from '../../../components/ui/button';
import { Spinner } from '../../../components/ui/spinner';
import { 
  UserIcon, 
  ServerIcon, 
  BuildingOfficeIcon,
  ArrowPathIcon as RefreshIcon
} from '../../../components/icons';

import EmpresasChart from './EmpresasChart';

type EmpresaRow = SuperAdminDashboardData['empresas'][0];

export const SuperAdminDashboard: React.FC = () => {
  const { data, isLoading, error, refetch } = useGetSuperAdminDashboardQuery();
  const [refreshDashboard, { isLoading: isRefreshing }] = useRefreshDashboardMutation();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  
  const handleRefresh = async () => {
    try {
      await refreshDashboard().unwrap();
      await refetch();
      showToast('Dashboard actualizado correctamente', 'success');
    } catch (error) {
      console.error('Error al actualizar dashboard:', error);
      showToast('Error al actualizar dashboard', 'error');
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="medium" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        <p>Error al cargar los datos del panel de control</p>
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
        <p>No hay datos disponibles en este momento</p>
      </div>
    );
  }
  
  // Calcular índices para la paginación
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmpresas = data.empresas.slice(startIndex, endIndex);
  const totalPages = Math.ceil(data.empresas.length / itemsPerPage);
  
  const tableColumns = [
    {
      header: 'Nombre',
      accessorKey: 'nombre' as keyof EmpresaRow
    },
    {
      header: 'Plan',
      accessorKey: 'plan' as keyof EmpresaRow
    },
    {
      header: 'Usuarios',
      accessorKey: 'usersCount' as keyof EmpresaRow,
      cell: (info: { getValue: () => unknown; row: { original: EmpresaRow } }) => 
        `${info.row.original.usersCount}/${info.row.original.maxUsers}`
    },
    {
      header: 'Bots Activos',
      accessorKey: 'activeBotsCount' as keyof EmpresaRow
    },
    { 
      header: 'Estado', 
      accessorKey: 'activo' as keyof EmpresaRow,
      cell: (info: { getValue: () => unknown; row: { original: EmpresaRow } }) => (
        <span className={`px-2 py-1 rounded text-xs ${info.row.original.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {info.row.original.activo ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Panel de Superadministrador</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? <Spinner size="small" /> : <RefreshIcon className="w-4 h-4 mr-2" />}
          Actualizar
        </Button>
      </div>
      
      {/* Estadísticas Generales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center">
            <BuildingOfficeIcon className="w-10 h-10 text-blue-500 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Empresas</p>
              <p className="text-2xl font-bold">{data.empresasCount}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center">
            <UserIcon className="w-10 h-10 text-green-500 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Usuarios Totales</p>
              <p className="text-2xl font-bold">{data.totalUsersCount}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center">
            <ServerIcon className="w-10 h-10 text-purple-500 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Bots Activos</p>
              <p className="text-2xl font-bold">{data.activeBotsCount}/{data.totalBots}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div>
            <p className="text-sm text-gray-500 mb-1">Uso del Servidor</p>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
              <div 
                className={`h-2.5 rounded-full ${
                  data.serverUsage > 80 ? 'bg-red-500' : 
                  data.serverUsage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`} 
                style={{ width: `${data.serverUsage}%` }}
              ></div>
            </div>
            <p className="text-xs text-right">{data.serverUsage}%</p>
          </div>
        </Card>
      </div>

      {/* Gráfico de Empresas */}
      <div className="mb-8">
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Distribución de usuarios por empresa</h2>
          <EmpresasChart data={data.empresasStats} />
        </Card>
      </div>

      {/* Tabla de Empresas */}
      <div className="mb-8">
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Listado de Empresas</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  {tableColumns.map((column, index) => (
                    <th
                      key={index}
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginatedEmpresas.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-muted/20">
                    {tableColumns.map((column, colIndex) => {
                      return (
                        <td key={colIndex} className="px-4 py-4 text-sm">
                          {column.cell
                            ? column.cell({ getValue: () => row[column.accessorKey], row: { original: row } })
                            : row[column.accessorKey]}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Paginación simple */}
          <div className="flex items-center justify-between border-t p-3 mt-4">
            <div className="text-sm text-muted-foreground">
              Página {currentPage} de {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-muted rounded-md disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm bg-muted rounded-md disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Actividad Reciente */}
      <div>
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Actividad Reciente del Sistema</h2>
          <div className="space-y-4">
            {data.systemActivity.map((item) => (
              <div key={item.id} className="border-b pb-3 last:border-b-0">
                <div className="flex justify-between">
                  <span className="font-medium">{item.action}</span>
                  <span className="text-sm text-gray-500">{new Date(item.timestamp).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                <p className="text-xs text-gray-500 mt-1">Usuario: {item.user}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}; 